import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { AvailabilitySettingsPanel } from "./AvailabilitySettingsPanel";
import type { AvailabilityProperty } from "./AvailabilityDrawer";
import { availabilityService } from "@/services/availabilityService";
import { usersService } from "@/services/usersService";
import { useUiStore } from "@/store/uiStore";
import { DEFAULT_SLOT_SETTINGS, resolveSlotSettings, type SlotSettings } from "@/types/availability";
import type { UserSummary } from "@/types/entities";
import { getApiErrorMessage } from "@/utils/apiErrors";

/**
 * Per-property slot settings editor: loads the cascade-resolved settings
 * (property → branch → legal entity → default) and upserts a property-scoped
 * override on save. These rules apply to every availability slot generated for
 * the property (slot interval, buffer, leads-per-slot, horizon, timezone).
 */
export function PropertySlotSettings({ property }: { property: AvailabilityProperty }) {
  const [settings, setSettings] = useState<SlotSettings>(DEFAULT_SLOT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [negotiators, setNegotiators] = useState<UserSummary[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const startPageLoading = useUiStore((s) => s.startPageLoading);
  const stopPageLoading = useUiStore((s) => s.stopPageLoading);

  const savedSettings = useRef<SlotSettings>(DEFAULT_SLOT_SETTINGS);

  const dirty = JSON.stringify(settings) !== JSON.stringify(savedSettings.current);

  useEffect(() => {
    if (!property.id) return;
    setLoadingSettings(true);
    availabilityService
      .getSettings({ propertyId: property.id, legalEntityId: property.legalEntityId ?? undefined })
      .then((s) => {
        const resolved = resolveSlotSettings(s);
        setSettings(resolved);
        savedSettings.current = resolved;
      })
      .catch((error) => {
        setSettings(DEFAULT_SLOT_SETTINGS);
        savedSettings.current = DEFAULT_SLOT_SETTINGS;
        toast.error(
          getApiErrorMessage(error) ?? "Failed to load slot settings.",
          { toastId: "property-slot-settings-load-error" },
        );
      })
      .finally(() => setLoadingSettings(false));
    setLoadingUsers(true);
    usersService
      .list({ legalEntityId: property.legalEntityId ?? undefined }, { page: 0, size: 100 })
      .then((res) => setNegotiators((res.content ?? []).filter((u) => u.sourceCrm != null)))
      .catch((error) => {
        setNegotiators([]);
        toast.error(
          getApiErrorMessage(error) ?? "Failed to load users for slot settings.",
          { toastId: "property-slot-settings-users-load-error" },
        );
      })
      .finally(() => setLoadingUsers(false));
  }, [property.id, property.legalEntityId]);

  // Drive the global PageLoader until both the slot-settings and negotiators
  // calls have completed.
  useEffect(() => {
    if (loadingSettings || loadingUsers) startPageLoading();
    else stopPageLoading();
  }, [loadingSettings, loadingUsers, startPageLoading, stopPageLoading]);

  // Ensure the loader is cleared if the drawer closes mid-request.
  useEffect(() => () => stopPageLoading(), [stopPageLoading]);

  async function save() {
    try {
      setSaving(true);
      const saved = await availabilityService.saveSettings({
        ...settings,
        propertyRefId: property.id,
        legalEntityId: property.legalEntityId ?? undefined,
      });
      // Keep the chosen default users visible even if the response omits them.
      const resolved = {
        ...resolveSlotSettings(saved),
        users: saved.users ?? settings.users,
      };
      setSettings(resolved);
      savedSettings.current = resolved;
      toast.success("Slot settings saved.");
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to save slot settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-md border border-bridge-border/40 bg-white p-4 sm:p-6 shadow-sm">
      <h3 className="text-sm font-semibold text-bridge-fg mb-1">Slot settings</h3>
      <p className="text-xs text-bridge-muted mb-3">
        Applies to all availability slots generated for this property.
      </p>
      <AvailabilitySettingsPanel
        value={settings}
        onChange={setSettings}
        onSave={save}
        saving={saving}
        users={negotiators}
        usersLoading={loadingUsers}
        saveDisabled={!dirty}
      />
    </div>
  );
}
